/**
 * @component SlideToConfirm
 * @description Wischgeste statt Tap fuer folgenschwere Aktionen (#1812, Bedienung/Optik #1824).
 *   Ein versehentlicher Tap auf "Ausstempeln" mitten in der Schicht erzeugt eine
 *   Fehlbuchung, die HR manuell korrigieren muss — eine bewusste Wischgeste passiert
 *   nicht aus Versehen.
 *
 *   EIN Eingabepfad: Pointer Events decken Finger, Maus und Stift ab. Parallele
 *   Touch- UND Maus-Handler waeren die klassische Quelle fuer Doppelausloesung, weil
 *   mobile Browser nach `touchend` zusaetzlich Maus-Events emulieren.
 *
 *   Der Slider haelt KEINE eigene Wahrheit ueber den Zustand der Aktion: er richtet
 *   sich allein nach dem Erfolgs-Boolean aus `onConfirm()`. Schlaegt der Serveraufruf
 *   fehl, federt er zurueck, statt still einzurasten.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronsRight } from 'lucide-react';

/** Anteil der Bahn, ab dem eingerastet wird — darunter federt der Griff zurueck. */
const SCHWELLE = 0.58;
const GRIFF_PX = 52;
const RAND_PX = 4;
/**
 * Mindest-Wischstrecke. Weil die GANZE Bahn den Zug annimmt, springt der Griff unter
 * den Finger — ohne diese Huerde wuerde ein blosser Tap am rechten Bahnende bereits
 * ueber der Schwelle liegen und buchen. Genau das soll die Geste verhindern.
 */
const MIN_ZUG_PX = 32;

export default function SlideToConfirm({
  label,
  loadingLabel,
  successLabel,
  ariaLabel,
  onConfirm,
  color = '#338edc',
  disabled = false,
}) {
  const bahnRef = useRef(null);
  const zugRef = useRef(null);
  const busyRef = useRef(false);
  const lebtRef = useRef(true);

  const [offset, setOffset] = useState(0);
  const [zieht, setZieht] = useState(false);
  const [busy, setBusy] = useState(false);
  const [erfolg, setErfolg] = useState(false);

  useEffect(() => () => { lebtRef.current = false; }, []);

  const maxOffset = useCallback(() => {
    const breite = bahnRef.current?.clientWidth ?? 0;
    return Math.max(0, breite - GRIFF_PX - RAND_PX * 2);
  }, []);

  const ausloesen = useCallback(async (max) => {
    if (busyRef.current || disabled) return;
    busyRef.current = true;
    setBusy(true);
    setOffset(max);
    if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(15);

    let ok = false;
    try {
      ok = await onConfirm();
    } catch {
      ok = false;
    }
    if (!lebtRef.current) return;

    if (ok) {
      // Erfolg kurz bestaetigen, dann in den neuen (servergedeckten) Zustand uebergehen.
      setErfolg(true);
      setTimeout(() => {
        if (!lebtRef.current) return;
        setErfolg(false);
        setOffset(0);
        busyRef.current = false;
        setBusy(false);
      }, 600);
    } else {
      setOffset(0);
      busyRef.current = false;
      setBusy(false);
    }
  }, [disabled, onConfirm]);

  // Die Handler sitzen auf der BAHN, nicht auf dem Griff: der Zug beginnt an jedem
  // Punkt der Flaeche, niemand muss den Knopf erst exakt treffen.
  const onPointerDown = (e) => {
    if (disabled || busyRef.current) return;
    const bahn = bahnRef.current;
    if (!bahn) return;
    const rect = bahn.getBoundingClientRect();
    const max = Math.max(0, rect.width - GRIFF_PX - RAND_PX * 2);
    if (max <= 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const start = Math.min(max, Math.max(0, e.clientX - rect.left - RAND_PX - GRIFF_PX / 2));
    zugRef.current = { startX: e.clientX, startOffset: start, max, offset: start, zug: 0 };
    setOffset(start);
    setZieht(true);
  };

  const onPointerMove = (e) => {
    const zug = zugRef.current;
    if (!zug) return;
    const delta = e.clientX - zug.startX;
    // Groesste erreichte Strecke, nicht die aktuelle: ein leichtes Zurueckwandern
    // am Ende der Geste soll die Buchung nicht entwerten.
    if (delta > zug.zug) zug.zug = delta;
    const naechster = Math.min(zug.max, Math.max(0, zug.startOffset + delta));
    zug.offset = naechster;
    setOffset(naechster);
  };

  const onPointerUp = () => {
    const zug = zugRef.current;
    if (!zug) return;
    zugRef.current = null;
    setZieht(false);
    if (zug.offset >= zug.max * SCHWELLE && zug.zug >= MIN_ZUG_PX) ausloesen(zug.max);
    else setOffset(0);
  };

  // Abgebrochene Geste: zurueckfedern, NICHT ausloesen.
  const onPointerCancel = () => {
    zugRef.current = null;
    setZieht(false);
    setOffset(0);
  };

  // AK4 (#1812): niemand darf durch die Geste vom Stempeln ausgeschlossen werden.
  const onKeyDown = (e) => {
    if (disabled || busyRef.current) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      ausloesen(maxOffset());
    }
  };

  const max = maxOffset();
  const anteil = max > 0 ? offset / max : 0;
  const text = erfolg ? successLabel : busy ? loadingLabel : label;
  const ruht = !zieht && !busy && !erfolg;

  return (
    <div
      ref={bahnRef}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={ariaLabel || label}
      aria-disabled={disabled || busy}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onKeyDown={onKeyDown}
      className="relative w-full overflow-hidden rounded-full select-none outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      style={{
        height: GRIFF_PX + RAND_PX * 2,
        // Kraeftig eingefaerbt statt grau: der Regler soll wie der fruehere Knopf
        // sofort als Aktion lesbar sein, nicht wie ein deaktiviertes Feld.
        background: color,
        // Die Bahn nimmt die Geste vollstaendig an. Mit `pan-y` uebernahm der Browser
        // die Bewegung als Seiten-Scroll und brach den Zug per pointercancel ab —
        // das war der Grund fuer "mehrere Anlaeufe". Gescrollt wird neben dem Regler.
        touchAction: 'none',
        opacity: disabled && !busy ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : zieht ? 'grabbing' : 'grab',
      }}
    >
      {/* Fortschritt: waechst mit der Wischstrecke, damit sichtbar ist, wie weit es noch ist */}
      <div
        className={`absolute inset-y-0 left-0 rounded-full ${zieht ? '' : 'transition-all duration-300'}`}
        style={{
          width: offset + GRIFF_PX + RAND_PX * 2,
          background: 'rgba(255, 255, 255, 0.26)',
        }}
      />

      {/* Beschriftung — blendet aus, waehrend gewischt wird */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none px-16 text-center"
        style={{ opacity: busy || erfolg ? 1 : 1 - anteil * 0.7 }}
      >
        <span className="text-sm font-semibold truncate" style={{ color: '#FFFFFF' }}>
          {text}
        </span>
      </div>

      {/* Griff */}
      <div
        aria-hidden="true"
        className={`absolute top-1 flex items-center justify-center rounded-full shadow-sm pointer-events-none ${
          zieht ? '' : 'transition-all duration-300'
        }`}
        style={{
          width: GRIFF_PX,
          height: GRIFF_PX,
          left: RAND_PX + offset,
          background: '#FFFFFF',
          color,
        }}
      >
        {erfolg
          ? <Check size={22} strokeWidth={2.5} />
          : busy
            ? <span className="w-5 h-5 rounded-full border-2 border-current border-t-transparent animate-spin" />
            : <ChevronsRight size={24} strokeWidth={2.5} className={ruht ? 'ws-slide-hint' : ''} />
        }
      </div>
    </div>
  );
}
